import React, { useState } from 'react'
import { Alert, Badge, Button, Card, Col, Form, Row } from 'react-bootstrap'
import CreatableSelect from 'react-select/creatable'

import ProviderService from '../../../services/ProviderService'

export default function MisclassifiedCard({ item, orgOptions = [], onResolvedChange }) {
  const isTargetIndiv = item.suggestedClassification === 'Individual'

  const [firstName, setFirstName] = useState(item.suggestedFirstName || '')
  const [lastName, setLastName] = useState(item.suggestedLastName || '')
  const [suffix, setSuffix] = useState(item.suggestedSuffix || '')
  const [orgName, setOrgName] = useState(item.suggestedOrgName || item.name || item.currentName || '')
  const [selectedOrg, setSelectedOrg] = useState(() => {
    if (item.suggestedAffiliationOrgId || item.suggestedAffiliationOrgName) {
      return {
        id: item.suggestedAffiliationOrgId || null,
        label: item.suggestedAffiliationOrgName,
        value: item.suggestedAffiliationOrgName,
      }
    }
    return null
  })

  const [isApplying, setIsApplying] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  const [error, setError] = useState(null)
  const [resolvedState, setResolvedState] = useState(null)

  const handleReclassify = async () => {
    setIsApplying(true)
    setError(null)
    try {
      const response = await ProviderService.reclassifyProvider({
        providerId: item.providerId,
        targetClassification: item.suggestedClassification,
        firstName: isTargetIndiv ? firstName.trim() : null,
        lastName: isTargetIndiv ? lastName.trim() : null,
        suffix: isTargetIndiv ? suffix.trim() : null,
        organizationName: isTargetIndiv
          ? selectedOrg
            ? selectedOrg.value || selectedOrg.label
            : null
          : orgName.trim(),
        affiliationOrgId: isTargetIndiv && selectedOrg ? selectedOrg.id : null,
        role: 'Primary Practice',
      })

      if (response && response.success) {
        setResolvedState({
          actionType: 'reclassify',
          undoActionId: response.undoActionId,
          undoPayloadJson: response.undoPayloadJson || response.undoActionId,
          message: response.message || `Reclassified as ${item.suggestedClassification}.`,
        })
        if (onResolvedChange) onResolvedChange(item.providerId, true)
      } else {
        setError(response?.message || 'Failed to reclassify provider.')
      }
    } catch (err) {
      setError(err?.message || 'Error occurred while reclassifying.')
    } finally {
      setIsApplying(false)
    }
  }

  const handleUndo = async () => {
    if (!resolvedState?.undoPayloadJson && !resolvedState?.undoActionId) return
    setIsUndoing(true)
    setError(null)
    try {
      await ProviderService.undoLibraryAction({
        actionType: resolvedState.actionType,
        undoActionId: resolvedState.undoActionId,
        previousStateJson: resolvedState.undoPayloadJson,
      })
      setResolvedState(null)
      if (onResolvedChange) onResolvedChange(item.providerId, false)
    } catch (err) {
      setError(err?.message || 'Failed to undo reclassification.')
    } finally {
      setIsUndoing(false)
    }
  }

  const handleKeepCurrent = () => {
    setResolvedState({
      actionType: 'dismiss',
      undoPayloadJson: null,
      message: `Kept current classification (${item.currentClassification}).`,
    })
    if (onResolvedChange) onResolvedChange(item.providerId, true)
  }

  return (
    <Card className={`mb-3 border-${resolvedState ? 'success' : 'light'} shadow-sm`}>
      <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
        <div className="d-flex align-items-center">
          <span className="mr-2" role="img" aria-label="reclassify">
            🔄
          </span>
          <strong className="text-dark">Misclassified Record</strong>
          <Badge variant="info" className="ml-2">
            Convert {item.currentClassification} → {item.suggestedClassification}
          </Badge>
        </div>
        {resolvedState && (
          <Badge variant="success" className="p-1 px-2">
            ✓ Resolved
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        {error && (
          <Alert variant="danger" className="py-1 small mb-2">
            {error}
          </Alert>
        )}

        {resolvedState ? (
          <div className="d-flex justify-content-between align-items-center py-2">
            <span className="text-success font-weight-bold">✓ {resolvedState.message}</span>
            {resolvedState.undoPayloadJson && (
              <Button variant="outline-secondary" size="sm" onClick={handleUndo} disabled={isUndoing}>
                {isUndoing ? 'Undoing...' : 'Undo'}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 className="mb-0 font-weight-bold text-dark">{item.name || item.currentName}</h6>
                <div className="small text-muted">{item.reason}</div>
              </div>
              <Badge variant="secondary">Currently: {item.currentClassification}</Badge>
            </div>

            {isTargetIndiv ? (
              <div className="bg-light p-2 rounded mb-2 border">
                <div className="small font-weight-bold text-secondary mb-1">
                  Proposed Individual Name & Organization Affiliation:
                </div>
                <Row className="mb-2">
                  <Col md={5}>
                    <Form.Group controlId={`first-${item.providerId}`} className="mb-0">
                      <Form.Label className="small mb-0 text-muted">First Name</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group controlId={`last-${item.providerId}`} className="mb-0">
                      <Form.Label className="small mb-0 text-muted">Last Name</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId={`suffix-${item.providerId}`} className="mb-0">
                      <Form.Label className="small mb-0 text-muted">Suffix</Form.Label>
                      <Form.Control size="sm" type="text" value={suffix} onChange={e => setSuffix(e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group controlId={`org-${item.providerId}`} className="mb-0">
                  <Form.Label className="small mb-0 text-muted">Organization Affiliation (Optional)</Form.Label>
                  <CreatableSelect
                    isClearable
                    placeholder="Search or enter organization to affiliate..."
                    options={orgOptions}
                    value={selectedOrg}
                    onChange={val => setSelectedOrg(val)}
                    onCreateOption={inputValue => {
                      const clean = (inputValue || '').trim()
                      if (clean) {
                        setSelectedOrg({ id: null, label: clean, value: clean })
                      }
                    }}
                    className="small"
                  />
                  {item.suggestedAffiliationOrgName && (
                    <div className="small text-success mt-1">
                      💡 Suggested organization: <strong>{item.suggestedAffiliationOrgName}</strong>
                    </div>
                  )}
                </Form.Group>
              </div>
            ) : (
              <div className="bg-light p-2 rounded mb-2 border">
                <Form.Group controlId={`orgName-${item.providerId}`} className="mb-0">
                  <Form.Label className="small mb-0 text-muted font-weight-bold">Proposed Organization Name</Form.Label>
                  <Form.Control size="sm" type="text" value={orgName} onChange={e => setOrgName(e.target.value)} />
                </Form.Group>
              </div>
            )}

            <div className="d-flex justify-content-end align-items-center mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="mr-2"
                onClick={handleKeepCurrent}
                disabled={isApplying}
              >
                Keep as {item.currentClassification}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleReclassify}
                disabled={isApplying || (isTargetIndiv ? !firstName.trim() && !lastName.trim() : !orgName.trim())}
              >
                {isApplying ? 'Converting...' : `Convert to ${item.suggestedClassification}`}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
